/**
 * Route Utilities
 * Centralized utilities for route detection, validation, and security
 */

/**
 * Admin route patterns - all routes that require global admin access
 */
export const ADMIN_ROUTE_PATTERNS = [
  '/admin',
  '/adminMenu',
  '/adminDashboard',
  '/adminEnvironmentConfig',
  '/adminEntityCatalogue',
  '/adminDefaultRooms',
  '/adminAllRooms',
  '/adminBillingSettings',
  '/adminServerJobs',
  '/adminMaps',
  '/adminRoomManagement',
  '/adminUserManagement',
  '/adminEndpointTesting',
  '/admin/product-updates',
  '/adminProductUpdates',
  '/adminMapEditorPlugins',
  '/routingTest',
] as const

/**
 * Admin route prefix - any route starting with this requires global admin
 */
export const ADMIN_ROUTE_PREFIX = '/admin'

/**
 * Check if a path is an admin route
 * Uses pattern matching for exact matches and prefix matching for nested routes
 */
export function isAdminRoute(path: string): boolean {
  if (!path || typeof path !== 'string') {
    return false
  }

  // Normalize path (remove trailing slashes, ensure leading slash)
  const normalizedPath = path.trim().replace(/\/+$/, '') || '/'
  
  // Check exact matches first (most common case)
  if (ADMIN_ROUTE_PATTERNS.includes(normalizedPath as any)) {
    return true
  }

  // Check prefix match for nested admin routes (e.g., /admin/something)
  if (normalizedPath.startsWith(ADMIN_ROUTE_PREFIX + '/') || 
      normalizedPath === ADMIN_ROUTE_PREFIX) {
    return true
  }

  return false
}

/**
 * Check if a path is a public route (no authentication required)
 * Note: This is a simple check. For comprehensive route metadata checking,
 * use the isPublicRoute from routeMetadata.ts which uses getRouteMetadata
 */
export function isPublicRouteSimple(path: string): boolean {
  if (!path || typeof path !== 'string') {
    return false
  }

  const publicRoutes = [
    '/',
    '/dashboard',
    '/publicLobby',
    '/join',
    '/room',
    '/rooms',
    '/myRooms',
    '/profile',
    '/profile/edit',
    '/settings',
  ]

  const normalizedPath = normalizeRoutePath(path)
  return publicRoutes.includes(normalizedPath)
}

/**
 * Check if a path is a company route
 */
export function isCompanyRoute(path: string): boolean {
  if (!path || typeof path !== 'string') {
    return false
  }

  const normalizedPath = path.trim().replace(/\/+$/, '') || '/'
  return normalizedPath.startsWith('/company')
}

/**
 * Normalize a route path
 * Removes trailing slashes, ensures leading slash, handles empty paths
 */
export function normalizeRoutePath(path: string): string {
  if (!path || typeof path !== 'string') {
    return '/'
  }

  // Remove trailing slashes (except for root)
  const trimmed = path.trim()
  if (trimmed === '' || trimmed === '/') {
    return '/'
  }

  // Ensure leading slash
  const normalized = trimmed.startsWith('/') ? trimmed : `/${trimmed}`
  
  // Remove trailing slashes
  return normalized.replace(/\/+$/, '')
}

/**
 * Extract route segments from a path
 * Example: '/admin/users/123' => ['admin', 'users', '123']
 */
export function getRouteSegments(path: string): string[] {
  const normalized = normalizeRoutePath(path)
  if (normalized === '/') {
    return []
  }
  return normalized.split('/').filter(segment => segment.length > 0)
}

/**
 * Get the base route (first segment)
 * Example: '/admin/users/123' => '/admin'
 */
export function getBaseRoute(path: string): string {
  const segments = getRouteSegments(path)
  if (segments.length === 0) {
    return '/'
  }
  return `/${segments[0]}`
}

/**
 * Check if a route matches a pattern (supports wildcards)
 * Example: matchRoutePattern('/admin/users', '/admin/*') => true
 */
export function matchRoutePattern(path: string, pattern: string): boolean {
  const normalizedPath = normalizeRoutePath(path)
  const normalizedPattern = normalizeRoutePath(pattern)

  // Exact match
  if (normalizedPath === normalizedPattern) {
    return true
  }

  // Wildcard pattern matching
  if (normalizedPattern.endsWith('/*')) {
    const basePattern = normalizedPattern.slice(0, -2)
    return normalizedPath.startsWith(basePattern + '/') || 
           normalizedPath === basePattern
  }

  // Prefix matching
  if (normalizedPath.startsWith(normalizedPattern + '/')) {
    return true
  }

  return false
}

/**
 * Security event types for logging
 */
export enum SecurityEventType {
  UNAUTHORIZED_ADMIN_ACCESS = 'unauthorized_admin_access',
  BLOCKED_ADMIN_ROUTE = 'blocked_admin_route',
  PERMISSION_CHECK_FAILED = 'permission_check_failed',
  ROUTE_GUARD_ERROR = 'route_guard_error',
}

/**
 * Log security events (can be extended to send to monitoring service)
 */
export function logSecurityEvent(
  type: SecurityEventType,
  details: {
    path: string
    userId?: string
    reason?: string
    timestamp?: string
  }
): void {
  const event = {
    type,
    ...details,
    timestamp: details.timestamp || new Date().toISOString(),
  }

  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.warn(`[Security] ${type}:`, event)
  }

  // In production, this could send to a monitoring service
  // Example: sendToMonitoringService(event)
}

